import React, { useContext } from 'react'
import {
  sessionContext,
  useGetAllBusinesses,
  useGetBusinessIfExists,
} from '@marketing-milk/frontend'
import { Business, BusinessError, Permissions } from '@marketing-milk/interfaces'
import { ErrorOutline } from '@mui/icons-material'

export type AccessErrorCause = 'authorizer' | 'adAccount' | 'unknown'

/**
 * `businesses.errors` carries no payload — it records that a Facebook call failed, not why. The
 * only signal available client-side is whether the businesses sharing this ad authorizer are also
 * failing, so every cause below is a hedge rather than a diagnosis.
 */
export function inferAccessCause(business: Business, allBusinesses: Business[]): AccessErrorCause {
  const authorizerID = business.fbAdAuthorizerID
  if (authorizerID === null || authorizerID === undefined) return 'unknown'

  const siblings = allBusinesses.filter(
    _ => _.id !== business.id && _.fbAdAuthorizerID === authorizerID
  )
  if (siblings.length === 0) return 'unknown'

  const allSiblingsFailing = siblings.every(_ =>
    (_.errors ?? []).includes(BusinessError.ad_account_access_error)
  )
  return allSiblingsFailing ? 'authorizer' : 'adAccount'
}

// Kept short on purpose — the banner costs vertical space on already-cramped pages, so it carries
// only what the admin panel can't work out for itself: whether the authorizer's other businesses
// are failing too. Everything else lives behind the link.
const accessCauseText: Record<AccessErrorCause, string> = {
  authorizer: 'likely the ad authorizer',
  adAccount: 'likely this ad account',
  unknown: 'cause unclear',
}

export function BusinessErrorBanner() {
  const business = useGetBusinessIfExists()
  const businesses = useGetAllBusinesses()
  const { user } = useContext(sessionContext)

  const errors = business?.errors ?? []
  if (!business || errors.length === 0) return null

  const hasAccessError = errors.includes(BusinessError.ad_account_access_error)
  const hasOfflineError = errors.includes(BusinessError.offline_upload_error)
  const accessCause = hasAccessError ? inferAccessCause(business, businesses) : undefined
  const canAccessAdmin = !!user?.permissions.includes(Permissions.AccessAdminApp)

  return (
    // Deliberately Tailwind rather than a MUI Alert. The app is wrapped in MUI v4's ThemeProvider
    // with no `StylesProvider injectFirst`, so v4's JSS sheets inject after v5's emotion sheets as
    // components mount. `.MuiPaper-root { background: #fff }` from v4 then beats a v5 Alert's
    // severity background on equal specificity, and the banner turns white after a navigation.
    <div
      role="alert"
      className="w-full flex items-center gap-3 px-4 py-2 bg-red-50 border-b-2 border-red-600 text-red-900"
    >
      <ErrorOutline className="text-red-600 flex-shrink-0" />
      <span>
        {accessCause && (
          <>
            <span className="font-semibold">
              Facebook is rejecting requests for this ad account
            </span>{' '}
            ({accessCauseText[accessCause]}).{' '}
          </>
        )}
        {hasOfflineError && (
          <>
            <span className="font-semibold">Offline conversion uploads are failing</span> — affects
            conversion-optimized campaigns only.{' '}
          </>
        )}
        Nothing is blocked.{' '}
        {canAccessAdmin ? (
          <>
            {/* The theme's anchor styling only shows on hover, which leaves the one action in this
                banner looking like plain text — underline it explicitly. */}
            <a
              href={`${process.env.REACT_APP_ADMIN}/business/beta/accounts/edit/${business.id}`}
              target="_blank"
              rel="noreferrer"
              className="underline font-semibold text-red-900 hover:text-red-700"
            >
              Fix it in the admin panel ↗
            </a>
            , then refresh this page.
          </>
        ) : (
          'Ask someone with admin access to resolve it.'
        )}
      </span>
    </div>
  )
}
